import { api, ApiError } from '../api/client'
import type { LocaleInfo, SubtitleInput } from '../api/types'
import { parseShowYaml, parseSubtitleName, readTextFile, type ParsedShowYaml } from './parse'

/** 与后端 maxSubtitleBody 一致：8 MiB */
export const MAX_SUB_BODY = 8 << 20

export interface SubDraft {
  season: number
  episode: number
  locale: string
  fileName: string
  content: string
  bytes: number
}

export interface ImportPack {
  show: ParsedShowYaml | null
  subs: SubDraft[]
  skipped: string[]
}

export interface SeasonBatch {
  season: number
  inputs: SubtitleInput[]
  bytes: number
}

function basename(path: string): string {
  const parts = path.replace(/\\/g, '/').split('/')
  return parts[parts.length - 1] || path
}

function isYamlName(name: string): boolean {
  return /\.ya?ml$/i.test(name)
}

function isSubName(name: string): boolean {
  return /\.(srt|vtt|txt|ass|ssa)$/i.test(name)
}

/** 详情页只补字幕时锁定剧 ID。 */
export async function buildImportPack(
  files: File[],
  opts?: { lockedShowId?: number; defaultSeason?: number },
): Promise<ImportPack> {
  const defaultSeason = opts?.defaultSeason ?? 1
  let show: ParsedShowYaml | null = null
  let yamlName: string | null = null
  const skipped: string[] = []
  const byKey = new Map<string, SubDraft>()

  for (const file of files) {
    const name = basename(file.name)
    if (name.startsWith('.') || name === 'Thumbs.db') continue

    if (isYamlName(name)) {
      try {
        const text = await readTextFile(file)
        const parsed = parseShowYaml(text)
        if (opts?.lockedShowId != null && parsed.id !== opts.lockedShowId) {
          skipped.push(`${name}（ID ${parsed.id} 与当前剧 #${opts.lockedShowId} 不符）`)
          continue
        }
        if (yamlName) skipped.push(`${yamlName}（被 ${name} 覆盖，采用 ID ${parsed.id}）`)
        yamlName = name
        show = parsed
      } catch (err) {
        skipped.push(`${name}（${err instanceof Error ? err.message : '解析失败'}）`)
      }
      continue
    }

    if (!isSubName(name)) {
      skipped.push(name)
      continue
    }

    if (file.size > MAX_SUB_BODY) {
      skipped.push(`${name}（过大，${humanSize(file.size)}，上限 ${humanSize(MAX_SUB_BODY)}）`)
      continue
    }

    const parsed = parseSubtitleName(name, defaultSeason)
    if (!parsed || !parsed.locale) {
      skipped.push(name)
      continue
    }
    try {
      const content = await readTextFile(file)
      const bytes = new Blob([content]).size
      if (bytes > MAX_SUB_BODY) {
        skipped.push(`${name}（过大，${humanSize(bytes)}，上限 ${humanSize(MAX_SUB_BODY)}）`)
        continue
      }
      const draft: SubDraft = {
        season: parsed.season,
        episode: parsed.episode,
        locale: parsed.locale,
        fileName: name,
        content,
        bytes,
      }
      byKey.set(`${draft.season}:${draft.episode}:${draft.locale}`, draft)
    } catch {
      skipped.push(name)
    }
  }

  const subs = [...byKey.values()].sort(
    (a, b) => a.season - b.season || a.episode - b.episode || a.locale.localeCompare(b.locale),
  )
  return { show, subs, skipped }
}

export function packSummary(pack: ImportPack) {
  const seasons = new Set(pack.subs.map((s) => s.season))
  const episodes = new Set(pack.subs.map((s) => `${s.season}:${s.episode}`))
  const locales = new Set(pack.subs.map((s) => s.locale))
  const bytes = pack.subs.reduce((sum, s) => sum + s.bytes, 0)
  return {
    seasons: seasons.size,
    episodes: episodes.size,
    locales: locales.size,
    files: pack.subs.length,
    bytes,
  }
}

export function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

export function oversizedSubtitleError(subs: SubDraft[]): string | null {
  const big = subs.filter((s) => s.bytes > MAX_SUB_BODY)
  if (big.length === 0) return null
  return `文件过大：${big.map((s) => `${s.fileName}（${humanSize(s.bytes)}）`).join('、')}，单文件上限 ${humanSize(MAX_SUB_BODY)}`
}

/** 按季拆批，单批 ≤ MAX_SUB_BODY。 */
export function planSeasonBatches(subs: SubDraft[]): SeasonBatch[] {
  const bySeason = new Map<number, SubDraft[]>()
  for (const s of subs) {
    if (s.bytes > MAX_SUB_BODY) continue
    const list = bySeason.get(s.season) ?? []
    list.push(s)
    bySeason.set(s.season, list)
  }

  const batches: SeasonBatch[] = []
  for (const season of [...bySeason.keys()].sort((a, b) => a - b)) {
    const list = bySeason.get(season) ?? []
    let current: SubDraft[] = []
    let currentBytes = 0
    const flush = () => {
      if (current.length === 0) return
      batches.push({
        season,
        bytes: currentBytes,
        inputs: current.map((d) => ({
          episode: d.episode,
          locale: d.locale,
          file_name: d.fileName,
          content: d.content,
        })),
      })
      current = []
      currentBytes = 0
    }
    for (const item of list) {
      if (current.length > 0 && currentBytes + item.bytes > MAX_SUB_BODY) flush()
      current.push(item)
      currentBytes += item.bytes
    }
    flush()
  }
  return batches
}

export interface ImportProgress {
  step: string
  done: number
  total: number
}

export interface ImportResult {
  showId: number
  uploaded: number
}

/** 部分步骤已成功，后续失败；不回滚。 */
export class ImportStepError extends Error {
  readonly showId: number
  readonly uploaded: number
  readonly failedSeason: number | null
  readonly uploadedSeasons: number[]
  readonly wroteShow: boolean

  constructor(opts: {
    showId: number
    uploaded: number
    failedSeason: number | null
    uploadedSeasons: number[]
    wroteShow: boolean
    cause: string
    done: number
    total: number
  }) {
    const seasons =
      opts.uploadedSeasons.length > 0 ? `已上传第 ${opts.uploadedSeasons.join('、')} 季字幕` : '尚未上传字幕'
    const showBit = opts.wroteShow ? `剧 #${opts.showId} 详情已写入，` : ''
    const failBit = opts.failedSeason != null ? `第 ${opts.failedSeason} 季失败：${opts.cause}` : opts.cause
    super(`${failBit}（${showBit}${seasons}；已完成 ${opts.done}/${opts.total} 步，未回滚）`)
    this.name = 'ImportStepError'
    this.showId = opts.showId
    this.uploaded = opts.uploaded
    this.failedSeason = opts.failedSeason
    this.uploadedSeasons = opts.uploadedSeasons
    this.wroteShow = opts.wroteShow
  }
}

export async function submitImportPack(
  pack: ImportPack,
  opts: {
    lockedShowId?: number
    genres?: string[]
    tags?: string[]
    onProgress?: (p: ImportProgress) => void
  } = {},
): Promise<ImportResult> {
  const showId = pack.show?.id ?? opts.lockedShowId
  if (showId == null) throw new Error('需要 YAML，或在剧详情页导入')

  const tooBig = oversizedSubtitleError(pack.subs)
  if (tooBig) throw new Error(tooBig)

  const batches = planSeasonBatches(pack.subs)
  const total = (pack.show ? 1 : 0) + batches.length
  let done = 0
  let uploaded = 0
  let wroteShow = false
  const uploadedSeasons: number[] = []

  if (pack.show) {
    opts.onProgress?.({ step: `写入剧 #${showId}`, done, total })
    const locales: LocaleInfo[] = pack.show.locales.map((l) => ({
      locale: l.locale,
      title: l.title,
      description: l.description,
    }))
    const genres = opts.genres ?? pack.show.genres
    const tags = opts.tags ?? pack.show.tags
    if (!genres || genres.length < 1) {
      throw new Error('请至少选择 1 个类型')
    }
    await api.upsertShow(showId, locales, {
      genres,
      tags: tags ?? [],
    })
    wroteShow = true
    done++
  }

  for (const batch of batches) {
    opts.onProgress?.({
      step: `上传第 ${batch.season} 季字幕（${batch.inputs.length} 条）`,
      done,
      total,
    })
    try {
      const result = await api.uploadSeasonSubs(showId, batch.season, batch.inputs)
      uploaded += result.uploaded
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '上传失败'
      throw new ImportStepError({
        showId,
        uploaded,
        failedSeason: batch.season,
        uploadedSeasons,
        wroteShow,
        cause: msg,
        done,
        total,
      })
    }
    uploadedSeasons.push(batch.season)
    done++
  }

  opts.onProgress?.({ step: '完成', done: total, total })
  return { showId, uploaded }
}

export async function collectDroppedFiles(dataTransfer: DataTransfer): Promise<File[]> {
  const items = dataTransfer.items
  if (!items || items.length === 0) return Array.from(dataTransfer.files)

  const out: File[] = []
  const entries: FileSystemEntry[] = []
  for (let i = 0; i < items.length; i++) {
    const entry = items[i].webkitGetAsEntry?.()
    if (entry) entries.push(entry)
    else {
      const f = items[i].getAsFile()
      if (f) out.push(f)
    }
  }
  if (entries.length === 0) return Array.from(dataTransfer.files)

  for (const entry of entries) {
    await walkEntry(entry, out)
  }
  return out
}

async function walkEntry(entry: FileSystemEntry, out: File[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => {
      ;(entry as FileSystemFileEntry).file(resolve, reject)
    })
    out.push(file)
    return
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader()
    const readBatch = (): Promise<FileSystemEntry[]> =>
      new Promise((resolve, reject) => reader.readEntries(resolve, reject))
    // readEntries 可能分批，循环直到空
    for (;;) {
      const batch = await readBatch()
      if (batch.length === 0) break
      for (const child of batch) await walkEntry(child, out)
    }
  }
}
